/**
 * AP-PORTAL-01: Portal Protected Route
 *
 * Validates the portal session via GET /api/portal/auth/me.
 * Redirects to /portal/login if session is invalid or expired.
 * Provides portalUser context to all child components.
 */
import { useState, useEffect, createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PortalUserContext = createContext(null);

export function usePortalUser() {
  const ctx = useContext(PortalUserContext);
  if (!ctx) throw new Error("usePortalUser must be used inside PortalProtectedRoute");
  return ctx;
}

export default function PortalProtectedRoute({ children }) {
  const [portalUser, setPortalUser] = useState(undefined);
  const location = useLocation();

  useEffect(() => {
    axios
      .get(`${API}/api/portal/auth/me`, { withCredentials: true })
      .then((res) => setPortalUser(res.data))
      .catch(() => setPortalUser(null));
  }, []);

  if (portalUser === undefined) {
    return (
      <div
        data-testid="portal-loading"
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#FBF7F2" }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: "#C9A15F", borderTopColor: "transparent" }}
          />
          <span className="text-sm" style={{ color: "#6B5B3E", fontFamily: "DM Sans, sans-serif" }}>
            Verifying session…
          </span>
        </div>
      </div>
    );
  }

  if (!portalUser) {
    return (
      <Navigate
        to="/portal/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return (
    <PortalUserContext.Provider value={{ portalUser, setPortalUser }}>
      {children}
    </PortalUserContext.Provider>
  );
}
